"use client";

import React, { useState, useEffect, useRef } from "react";
import { Box, Button } from "@mui/material";
import { CartItem, updateCart } from "../../app/services/CartService";
import { toast } from "react-toastify";
import { ApiResponse } from "../../app/models/ApiResponse";
import { useStore } from "../../app/stores/store";

interface Props {
  cartItem: CartItem;
}

const CartQuantityManager: React.FC<Props> = ({ cartItem }) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const { cartStore } = useStore();
  const [quantity, setQuantity] = useState<number>(cartItem.quantity);
  const [inputValue, setInputValue] = useState<string>(
    String(cartItem.quantity)
  );

  useEffect(() => {
    setQuantity(cartItem.quantity);
    setInputValue(String(cartItem.quantity));
  }, [cartItem]);

  const updateCartQuantity = React.useCallback(
    async (newQty: number) => {
      try {
        const res = (await updateCart({
          cart_id: cartItem.id,
          quantity: newQty,
        })) as ApiResponse<any>;
        if (res.status === "success") {
          cartStore.loadCartItems(cartItem.outlet_id);
          toast.success("Cart updated successfully");
        } else {
          toast.error(res.message || "Failed to update cart");
        }
      } catch (error) {
        toast.error("Failed to update cart quantity");
      }
    },
    [cartItem.id, cartItem.outlet_id, cartStore]
  );

  const validateQuantity = React.useCallback(() => {
  const newQty = Number(inputValue);
  if (!isNaN(newQty) && newQty >= 1 && newQty <= 1000) {
    if (newQty !== quantity) {
      setQuantity(newQty);
      updateCartQuantity(newQty);
    }
  } else {
    alert("Quantity must be between 1 and 1000");
    setInputValue(String(quantity)); // reset to old value
  }
}, [inputValue, quantity, updateCartQuantity]);

  const handleClickOutside = React.useCallback((event: MouseEvent) => {
    if (
      inputRef.current &&
      !inputRef.current.contains(event.target as Node)
    ) {
      validateQuantity();
    }
  }, [inputRef, validateQuantity]);

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [handleClickOutside]);

  const handleIncrease = () => {
    const newQty = quantity + 1;
    setQuantity(newQty);
    setInputValue(String(newQty));
    updateCartQuantity(newQty);
  };

  const handleDecrease = () => {
    if (quantity > 1) {
      const newQty = quantity - 1;
      setQuantity(newQty);
      setInputValue(String(newQty));
      updateCartQuantity(newQty);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (val === "" || /^\d{1,3}$/.test(val)) {
      setInputValue(val);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      validateQuantity();
    }
  };

  const quantityButtonSx = {
    minWidth: 30,
    height: 30,
    borderRadius: 0,
    backgroundColor: "#006837",
    color: "#fff",
    fontSize: 24,
    "&:hover": {
      backgroundColor: "#004d29",
    },
  };

  return (
    <Box display="flex" alignItems="center" mt={1}>
      <Button
        sx={quantityButtonSx}
        onClick={handleDecrease}
        disabled={quantity <= 1}
      >
        −
      </Button>
      <input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        style={{
          width: 50,
          textAlign: "center",
          border: "1px solid #ccc",
          height: 30,
          WebkitAppearance: "none",
        }}
      />
      <Button
        sx={quantityButtonSx}
        onClick={handleIncrease}
        disabled={quantity >= 999}
      >
        +
      </Button>
    </Box>
  );
};

export default CartQuantityManager;
